import { useState } from 'react'
import type Team from '../../model/Team'
import { useModal } from '../../contexts/ModalContext'
import { useTeams } from '../../hooks/team/useTeams'
import { useUpdateTeamName } from '../../hooks/team/useUpdateTeamName'
import { TEAM_NAME_MAX_LENGTH, validateTeamName } from '../../logic/teamNameUtils'
import { logError } from '../../util/errorLogger'
import Overlay, { OverlayCard } from '../primitives/Overlay'
import TextField from '../primitives/TextField'
import styles from './TeamScreen.module.css'

const UNIQUE_VIOLATION = '23505'

interface TeamRenameModalProps {
  team: Team
  onClose: () => void
}

export default function TeamRenameModal({ team, onClose }: TeamRenameModalProps) {
  const { showSnackbar } = useModal()
  const { data: teams, isLoading: isLoadingTeams } = useTeams(team.challengeID)
  const { mutate: updateTeamName, isPending } = useUpdateTeamName(team.id)
  const [name, setName] = useState(team.name)
  const [nameError, setNameError] = useState('')

  const teamName = name.trim()
  const isUnchanged = teamName === team.name

  function onSubmit(e: React.FormEvent) {
    e.preventDefault()

    const error = validateTeamName(
      teamName,
      (teams ?? []).filter((other) => other.id !== team.id).map((other) => other.name),
    )
    if (error) {
      setNameError(error)
      return
    }

    updateTeamName(teamName, {
      onSuccess: () => {
        showSnackbar('team-renamed', 'Team name updated', 'success')
        onClose()
      },
      onError: (saveError) => {
        if ((saveError as { code?: string }).code === UNIQUE_VIOLATION) {
          setNameError('Name is already taken')
          return
        }
        void logError(saveError, { component: 'TeamRenameModal', action: 'rename' })
        setNameError('Could not update the team name. Try again.')
      },
    })
  }

  return (
    <Overlay variant="backdrop" onClose={onClose} ariaLabel="Rename team">
      <OverlayCard>
        <div className={styles.modalEyebrow}>Team leader</div>
        <div className={styles.modalTitle}>Rename your team</div>
        <form className={styles.renameForm} onSubmit={onSubmit}>
          <TextField
            label="Team name"
            value={name}
            onChange={(e) => {
              setName(e.target.value)
              setNameError('')
            }}
            error={nameError}
            autoFocus
            maxLength={TEAM_NAME_MAX_LENGTH}
          />
          <p className={styles.modalSub}>Keep it PG-13, kids can do Relentless too!</p>
          <button
            type="submit"
            className={[styles.gbtn, styles.gbtnPrimary].join(' ')}
            disabled={isLoadingTeams || isPending || isUnchanged || !teamName}
          >
            {isPending ? 'Saving…' : 'Save name'}
          </button>
        </form>
        <button type="button" className={styles.modalDismiss} onClick={onClose}>
          Cancel
        </button>
      </OverlayCard>
    </Overlay>
  )
}
